/**
 * GpuTimer — real GPU frame/pass timing via WebGPU timestamp queries.
 *
 * Electron/Dawn does not expose `GPUCommandEncoder.writeTimestamp`, so timing is
 * done with per-pass `timestampWrites` (a begin/end query pair per render or
 * compute pass) written into a single {@link GPUQuerySet}. At end-of-frame the
 * whole used range is resolved into a buffer, copied to a mappable readback
 * buffer and read back asynchronously — so results arrive ~1–2 frames late,
 * which is fine for a profiler HUD.
 *
 * Everything is best-effort and self-disabling: if the feature is missing, a
 * query set can't be created, or any resolve/readback throws, the timer turns
 * itself off and rendering is completely unaffected (pass descriptors simply get
 * no `timestampWrites`).
 *
 * @module @engine/render2d
 */

/** A begin/end timestamp pair suitable for both render- and compute-pass descriptors. */
export interface TimestampPair {
  querySet: GPUQuerySet;
  beginningOfPassWriteIndex: number;
  endOfPassWriteIndex: number;
}

/** Per-pass GPU time for the frame (grouped by marker label). */
export interface GpuPassTime {
  name: string;
  ms: number;
}

const NS_TO_MS = 1e-6;
/** Readback buffers cycled to avoid mapping one that's still in flight. */
const READBACK_POOL = 3;

interface Readback {
  buffer: GPUBuffer;
  busy: boolean;
  pairs: { label: string; begin: number; end: number }[];
  count: number;
}

export class GpuTimer {
  private readonly device: GPUDevice;
  private readonly querySet: GPUQuerySet;
  private readonly resolveBuffer: GPUBuffer;
  private readonly readbacks: Readback[] = [];
  private readonly capacityQueries: number;

  private used = 0;
  private curPairs: { label: string; begin: number; end: number }[] = [];
  private pending: Readback | null = null;
  private disabled = false;

  /** Last completed frame's total GPU time (min begin → max end), in ms. */
  lastGpuTimeMs = 0;
  /** Last completed frame's per-marker GPU times, sorted descending. */
  lastPasses: GpuPassTime[] = [];

  /** Create a timer, or null if timestamp queries are unavailable/unsupported. */
  static tryCreate(device: GPUDevice, maxPairs = 96): GpuTimer | null {
    try {
      if (!device.features?.has('timestamp-query')) return null;
      return new GpuTimer(device, maxPairs);
    } catch {
      return null;
    }
  }

  private constructor(device: GPUDevice, maxPairs: number) {
    this.device = device;
    const count = maxPairs * 2;
    this.capacityQueries = count;
    this.querySet = device.createQuerySet({ type: 'timestamp', count, label: 'profiler-gpu-timer' });
    this.resolveBuffer = device.createBuffer({
      size: count * 8,
      usage: GPUBufferUsage.QUERY_RESOLVE | GPUBufferUsage.COPY_SRC,
      label: 'gpu-timer-resolve',
    });
    for (let i = 0; i < READBACK_POOL; i++) {
      this.readbacks.push({
        buffer: device.createBuffer({
          size: count * 8,
          usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
          label: `gpu-timer-readback-${i}`,
        }),
        busy: false,
        pairs: [],
        count: 0,
      });
    }
  }

  /** Reset per-frame allocation. Call from the context's beginFrame. */
  beginFrame(): void {
    if (this.disabled) return;
    this.used = 0;
    this.curPairs = [];
  }

  /**
   * Allocate a begin/end query pair for a pass under the given marker label.
   * Assign the result to the pass descriptor's `timestampWrites`. Returns
   * undefined when disabled or the per-frame capacity is exhausted (the pass
   * then simply isn't timed — no error).
   */
  allocPair(label: string): TimestampPair | undefined {
    if (this.disabled || this.used + 2 > this.capacityQueries) return undefined;
    const begin = this.used;
    const end = this.used + 1;
    this.used += 2;
    this.curPairs.push({ label, begin, end });
    return { querySet: this.querySet, beginningOfPassWriteIndex: begin, endOfPassWriteIndex: end };
  }

  /** True when at least one pass was timed this frame. */
  hasPending(): boolean {
    return !this.disabled && this.used > 0;
  }

  /**
   * Record resolve + copy into a free readback buffer. Must be called with an
   * encoder that will be submitted this frame (the query set retains values
   * across the frame's submits, so a single resolve at end-of-frame is correct
   * even when flushCompute split the frame into multiple command buffers).
   */
  resolve(encoder: GPUCommandEncoder): void {
    this.pending = null;
    if (this.disabled || this.used === 0) return;
    const slot = this.readbacks.find(r => !r.busy);
    if (!slot) return; // all readback buffers in flight — skip this frame
    try {
      encoder.resolveQuerySet(this.querySet, 0, this.used, this.resolveBuffer, 0);
      encoder.copyBufferToBuffer(this.resolveBuffer, 0, slot.buffer, 0, this.used * 8);
      slot.pairs = this.curPairs.slice();
      slot.count = this.used;
      slot.busy = true;
      this.pending = slot;
    } catch {
      this.disabled = true;
      this.pending = null;
    }
  }

  /** Kick the async map on the resolved buffer. Call after the frame is submitted. */
  readback(): void {
    const slot = this.pending;
    this.pending = null;
    if (!slot || this.disabled) return;
    slot.buffer.mapAsync(GPUMapMode.READ).then(() => {
      try {
        const copy = slot.buffer.getMappedRange(0, slot.count * 8).slice(0);
        this.consume(slot, new BigInt64Array(copy));
      } catch {
        /* ignore a single bad frame */
      } finally {
        try { slot.buffer.unmap(); } catch { /* noop */ }
        slot.busy = false;
      }
    }).catch(() => { slot.busy = false; });
  }

  private consume(slot: Readback, data: BigInt64Array): void {
    let minBegin = -1n;
    let maxEnd = -1n;
    const byLabel = new Map<string, bigint>();
    for (const p of slot.pairs) {
      const b = data[p.begin];
      const e = data[p.end];
      if (b === undefined || e === undefined || e <= b) continue; // unwritten / invalid
      byLabel.set(p.label, (byLabel.get(p.label) ?? 0n) + (e - b));
      if (minBegin < 0n || b < minBegin) minBegin = b;
      if (maxEnd < 0n || e > maxEnd) maxEnd = e;
    }
    this.lastGpuTimeMs = minBegin >= 0n && maxEnd > minBegin ? Number(maxEnd - minBegin) * NS_TO_MS : 0;
    this.lastPasses = [...byLabel.entries()]
      .map(([name, ns]) => ({ name, ms: Number(ns) * NS_TO_MS }))
      .sort((a, b) => b.ms - a.ms);
  }

  destroy(): void {
    this.disabled = true;
    try {
      this.querySet.destroy();
      this.resolveBuffer.destroy();
      for (const r of this.readbacks) r.buffer.destroy();
    } catch {
      /* noop */
    }
  }
}
